/**
 * @flow
 */

import React from 'react';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Button, Icon, Popup, Message } from 'semantic-ui-react';
import styled from 'styled-components';

import type { Appointment } from '../../../../type';
import { GET_INVOICE_QUERY } from './invoice/AppointmentInvoice';

type CreateInvoiceButtonProps = {
    appointment: Appointment,
    disabled: boolean,
    onClicked: Function,
};

// A mutation that creates a new invoice
const CREATE_INVOICE_MUTATION = gql`
    mutation createInvoice($input: CreateInvoiceInput!, $lang: String!) {
        createInvoice(input: $input) {
            invoice {
                id
                items {
                    id
                    type
                    entity {
                        ... on Service {
                            id
                            name(lang: $lang)
                        }
                        ... on Product {
                            id
                            name
                        }
                    }
                    seller
                    sellerType
                    quantity
                    price
                    paymentType
                    appointment {
                        id
                    }
                }
                totalPrice
                paid
            }
        }
    }
`;

const StyledButton = styled(Button)`
    opacity: 0.45;
    cursor: default !important;
`;

const DisabledButton = props => (
    <StyledButton color="green" floated="right" {...props}>
        <Icon name="money" />
        <FormattedMessage
            id="pages.appointments.details.CreateInvoiceButton.create"
            defaultMessage="Create Invoice"
        />
    </StyledButton>
);

const CreateInvoiceButton = ({
    appointment,
    disabled,
    onClicked,
}: CreateInvoiceButtonProps) => {
    const salon = useSelector(({ user }) => user.get('salon'));
    const currentLanguage = useSelector(
        ({ intl }) => intl.get('locale') || intl.get('defaultLanguage'),
    );
    const [createInvoice, { loading }] = useMutation(CREATE_INVOICE_MUTATION);

    const onClick = () => {
        const variables = {
            salon,
            appointment: appointment.id,
            lang: currentLanguage,
            client: appointment.client.id,
        };
        return createInvoice({
            variables: {
                input: {
                    salon,
                    appointment: appointment.id,
                },
                lang: currentLanguage,
            },
            // Link the new invoice onto the appointment in the cache (immutable
            // rewrite — readQuery results are frozen in AC3).
            update: (store, { data: { createInvoice: result } }) => {
                try {
                    const data = store.readQuery({
                        query: GET_INVOICE_QUERY,
                        variables,
                    });
                    if (!data) return;
                    store.writeQuery({
                        query: GET_INVOICE_QUERY,
                        variables,
                        data: {
                            ...data,
                            viewer: {
                                ...data.viewer,
                                salon: {
                                    ...data.viewer.salon,
                                    appointment: {
                                        ...data.viewer.salon.appointment,
                                        invoice: result.invoice,
                                    },
                                },
                            },
                        },
                    });
                } catch (e) {
                    // It's safe to ignore this exception
                }
            },
        })
            .then(() => {
                // Switch to Invoice tab after it was created.
                onClicked();
            })
            .catch(error => {
                const graphQLError =
                    error.graphQLErrors && error.graphQLErrors[0];
                throw new Error(
                    'Unable to create invoice. [' +
                        (graphQLError && graphQLError.message) +
                        ']',
                );
            });
    };

    return disabled ? (
        <Popup
            style={{ color: 'red', padding: 0 }}
            trigger={<DisabledButton />}
        >
            <Message error>
                <Icon name="warning sign" />
                <FormattedMessage
                    id="pages.appointments.details.CreateInvoiceButton.IncompleteActions"
                    defaultMessage="To create invoice, please apply current appointment changes"
                />
            </Message>
        </Popup>
    ) : (
        <Button
            onClick={onClick}
            loading={loading}
            disabled={disabled}
            color="green"
            floated="right"
        >
            <Icon name="money" />
            <FormattedMessage
                id="pages.appointments.details.CreateInvoiceButton.create"
                defaultMessage="Create Invoice"
            />
        </Button>
    );
};

export default CreateInvoiceButton;
